import React, { useState } from "react";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const INQUIRY_TYPES = [
  "General Question","Request a Quote","Existing Client Support",
  "Government / RFP","Careers","Vendor / Supplier","Other",
];
const CONTACT_METHODS = ["Phone","Email","Either"];
const BEST_TIMES = ["Morning","Afternoon","Evening","Anytime"];

const INITIAL = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [inquiryType, setInquiryType] = useState("");
  const [contactMethod, setContactMethod] = useState("");
  const [bestTime, setBestTime] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please complete name, email, and message.");
    setBusy(true);
    try {
      const extras = [];
      if (inquiryType) extras.push(`Inquiry Type: ${inquiryType}`);
      if (contactMethod) extras.push(`Preferred Contact Method: ${contactMethod}`);
      if (bestTime) extras.push(`Best Time to Contact: ${bestTime}`);
      const message = extras.length ? `${form.message}\n\n---\n${extras.join("\n")}` : form.message;
      await submitContact({ ...form, subject: form.subject || inquiryType, message });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Message received — we'll reply within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message. Please try again or call us.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success-message" className="bg-white border border-[var(--es-border)] rounded-sm p-10 md:p-14 text-center">
        <CheckCircle size={64} weight="fill" className="mx-auto text-[var(--es-accent)]" />
        <h3 className="mt-6 text-3xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Thank you for contacting Empire Serv.</h3>
        <p className="mt-4 text-[var(--es-text-muted)] max-w-xl mx-auto">
          Your message has been received. A member of our team will reach out within one business day.
        </p>
        <p className="mt-6 text-sm uppercase tracking-[0.25em] font-semibold text-[var(--es-accent)]">
          Be the Solution · Be Excellent · Be Reliable · Be Valuable
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={onSubmit} className="bg-white border border-[var(--es-border)] rounded-sm p-6 md:p-10 space-y-10">
      <Section title="Your Information" number="1">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Full Name *"><input required className="es-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-input-name" /></Field>
          <Field label="Email Address *"><input required type="email" className="es-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-input-email" /></Field>
          <Field label="Phone Number"><input type="tel" className="es-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-input-phone" /></Field>
          <Field label="Company or Organization"><input className="es-input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="contact-input-company" /></Field>
        </div>
      </Section>

      <Section title="How Can We Help?" number="2">
        <div className="es-label">Inquiry Type</div>
        <RadioGrid items={INQUIRY_TYPES} selected={inquiryType} onSelect={setInquiryType} testPrefix="contact-inquiry-type" cols="grid-cols-2 md:grid-cols-3" />

        <div className="mt-5">
          <Field label="Subject"><input className="es-input" placeholder="Brief subject line" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-input-subject" /></Field>
        </div>
        <div className="mt-5">
          <Field label="Message *">
            <textarea rows={6} required className="es-input" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-input-message" />
          </Field>
        </div>
      </Section>

      <Section title="Preferred Follow-Up" number="3">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="es-label">Preferred Contact Method</div>
            <RadioGrid items={CONTACT_METHODS} selected={contactMethod} onSelect={setContactMethod} testPrefix="contact-method" cols="grid-cols-3" />
          </div>
          <div>
            <div className="es-label">Best Time to Contact You</div>
            <RadioGrid items={BEST_TIMES} selected={bestTime} onSelect={setBestTime} testPrefix="contact-best-time" cols="grid-cols-2 md:grid-cols-4" />
          </div>
        </div>
      </Section>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[var(--es-border)]">
        <p className="text-xs text-[var(--es-text-muted)] max-w-md">
          By submitting, you consent to be contacted by Empire Serv. We respond within one business day.
        </p>
        <button type="submit" disabled={busy} data-testid="contact-form-submit-button" className="es-btn-accent whitespace-nowrap disabled:opacity-50">
          {busy ? "Sending…" : "Send Message"} <ArrowRight size={18} />
        </button>
      </div>
    </form>
  );
}

function Section({ title, number, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 bg-[var(--es-navy)] text-[var(--es-accent)] rounded-sm flex items-center justify-center font-bold text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{number}</span>
        <h3 className="text-xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="es-label">{label}</span>
      {children}
    </label>
  );
}

function RadioGrid({ items, selected, onSelect, testPrefix, cols }) {
  return (
    <div className={`grid ${cols} gap-2 mt-2`}>
      {items.map((v) => {
        const on = selected === v;
        const id = `${testPrefix}-${v.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <button type="button" key={v} data-testid={id} onClick={() => onSelect(v)}
            className={`text-xs md:text-sm text-left border px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${on ? "border-[var(--es-navy)] bg-[var(--es-navy)] text-white" : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]"}`}>
            <span className={`w-3.5 h-3.5 border rounded-full flex items-center justify-center flex-shrink-0 ${on ? "border-[var(--es-accent)]" : "border-[var(--es-border)]"}`}>
              {on && <span className="w-1.5 h-1.5 bg-[var(--es-accent)] rounded-full" />}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}
